import { formatHostOffice } from "@/lib/ops/hostSalesperson";
import { sendInvite } from "@/components/admin/adminContactsInviteClient";
import type { DealSalespersonState } from "./opsPaperworkTypes";

function SalespersonAlertChip(props: {
  salesperson: DealSalespersonState;
  busy: boolean;
  onInvite?: () => void;
  onRequest?: () => void;
  onAddLocation?: () => void;
}) {
  const s = props.salesperson;
  const org = s.invite_org_name;
  const chip =
    "flex h-full min-h-0 w-[9.5rem] shrink-0 flex-col justify-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] leading-tight text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100";
  const btn = "rounded bg-indigo-600 px-2 py-1 text-[11px] text-white disabled:opacity-50";

  if (s.status === "linked" && s.location_id && s.on_location === false) {
    return (
      <div className={chip}>
        <p>Not on this location</p>
        {props.onAddLocation && s.can_add_location ? (
          <button type="button" disabled={props.busy} onClick={props.onAddLocation} className={btn}>
            Add to this location
          </button>
        ) : null}
      </div>
    );
  }
  if (s.status === "linked") return null;

  const statusLine =
    s.status === "invite_requested"
      ? "Invite requested"
      : s.status === "invited"
        ? "Invite sent"
        : "This user is not an app user";

  return (
    <div className={chip}>
      <p>{statusLine}</p>
      {!s.host.email ? <p className="text-amber-800 dark:text-amber-200">No email on this deal</p> : null}
      {s.can_invite && s.host.email && s.status !== "invited" ? (
        <button type="button" disabled={props.busy} onClick={props.onInvite} className={btn}>
          Invite to {org}
        </button>
      ) : null}
      {!s.can_invite && s.status === "missing" ? (
        <button
          type="button"
          disabled={props.busy || !s.host.email}
          onClick={props.onRequest}
          className="rounded border border-amber-400 bg-white px-2 py-1 text-[11px] text-amber-950 disabled:opacity-50 dark:bg-zinc-900 dark:text-amber-100"
        >
          Request invite
        </button>
      ) : null}
    </div>
  );
}

export function OpsDealIdentityStrip(props: {
  salesperson: DealSalespersonState;
  busy?: boolean;
  onInvite?: () => void;
  onRequest?: () => void;
  onAddLocation?: () => void;
}) {
  const s = props.salesperson;
  const name = (s.profile?.full_name || s.profile?.display_name || s.host.name || s.host.email || "Unknown").trim();
  const office = formatHostOffice(s.host) || s.location_name || "";
  const email = s.host.email && s.host.email.toLowerCase() !== name.toLowerCase() ? s.host.email : "";
  const alert = (
    <SalespersonAlertChip
      salesperson={s}
      busy={props.busy === true}
      onInvite={props.onInvite}
      onRequest={props.onRequest}
      onAddLocation={props.onAddLocation}
    />
  );
  return (
    <div className="inline-flex min-w-0 max-w-xl items-stretch gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">
      <div className="min-w-0 flex-1">
        <p className="min-w-0 break-words">
          <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Salesperson</span>
          {" "}
          {name}
          {email ? <span className="text-gray-600 dark:text-zinc-300"> · {email}</span> : null}
        </p>
        {office ? (
          <p className="mt-0.5 min-w-0 break-words">
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Office</span>
            {" "}
            {office}
          </p>
        ) : null}
      </div>
      {alert}
    </div>
  );
}

export async function inviteDealSalesperson(args: {
  token: string;
  salesperson: DealSalespersonState;
}): Promise<void> {
  const email = args.salesperson.host.email.trim();
  if (!email) throw new Error("Host payload has no salesperson email.");
  await sendInvite({
    token: args.token,
    email,
    orgId: args.salesperson.invite_org_id,
    role: "sales",
    allowPotentialDuplicate: false,
    migrateExternalContactId: "",
  });
}
